import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/common/Button";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { ChecklistRow, type RowState } from "../primitives/ChecklistRow";

/**
 * Second step — animated parallel detection of Claude CLI, git (Win only),
 * and outbound network.
 *
 * The rows start in a pending state, flip to "checking" for a short
 * stagger of ~200ms each (to feel active), and then settle based on the
 * real environment response from the backend.
 */
export function EnvironmentCheckStep() {
  const {
    environment,
    envLoading,
    envError,
    refreshEnvironment,
    goNext,
    installQueue,
  } = useOnboardingStore();

  // Stagger — each row flips from "pending" → "checking" → final.
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // If we already have an environment result from a prior call, still
    // run a short animation so the user perceives the check happening.
    const timers: number[] = [];
    setProgress(0);
    timers.push(window.setTimeout(() => setProgress(1), 220));
    timers.push(window.setTimeout(() => setProgress(2), 440));
    timers.push(window.setTimeout(() => setProgress(3), 660));
    return () => timers.forEach(window.clearTimeout);
  }, [environment?.platform.os]);

  // "Ready" for the Connect step means: nothing left to install. The
  // backend's `environment.ready` only checks runtime usability — but
  // `installQueue` also includes a user-facing `claude` install when only
  // the SDK-bundled binary is present, since the user needs `claude` on
  // their shell PATH as the chat runtime claude-agent-sdk spawns.
  const nothingToInstall = installQueue.length === 0;
  const fullyReady = (environment?.ready ?? false) && nothingToInstall;

  // Auto-advance once everything is ready — gentle pause so the user
  // sees the final "ready" state before the slide.
  useEffect(() => {
    if (!environment || envLoading) return;
    if (fullyReady && progress >= 3) {
      const id = window.setTimeout(() => goNext(), 900);
      return () => window.clearTimeout(id);
    }
  }, [environment, envLoading, progress, goNext, fullyReady]);

  const isWindows = environment?.platform.os === "windows";

  const rows = useMemo(() => {
    const built: Array<{
      key: string;
      label: string;
      icon: React.ReactNode;
      state: RowState;
      detail?: string;
    }> = [];

    // 1. Claude CLI
    built.push({
      key: "claude",
      label: "Claude Code",
      icon: <ClaudeIcon />,
      state: resolveClaudeState(environment, progress, envLoading),
      detail: resolveClaudeDetail(environment, progress),
    });

    // 2. Git (Windows only)
    if (isWindows) {
      built.push({
        key: "git",
        label: "Git for Windows",
        icon: <GitIcon />,
        state: resolveGitState(environment, progress, envLoading),
        detail: resolveGitDetail(environment, progress),
      });
    }

    // 3. Network
    built.push({
      key: "network",
      label: "Internet connection",
      icon: <NetworkIcon />,
      state: resolveNetworkState(environment, progress, envLoading),
      detail: resolveNetworkDetail(environment, progress),
    });

    return built;
  }, [environment, progress, envLoading, isWindows]);

  const allChecked = progress >= 3 && environment !== null;
  const hasMissing = allChecked && !fullyReady;

  return (
    <div className="w-full">
      <h1 className="text-[32px] font-bold leading-[1.1] tracking-[-0.01em] text-space">
        {!allChecked
          ? "Checking your machine…"
          : fullyReady
            ? "Everything I need is already here."
            : "A couple of things to set up"}
      </h1>
      <p className="mt-1.5 text-[14px] leading-relaxed text-space-50">
        {!allChecked
          ? "This takes a moment — I'm looking for Claude Code and checking your connection."
          : fullyReady
            ? "Nice. I'll pull your workspace access down next."
            : `I'll handle the ${installQueue.length > 1 ? "missing pieces" : "missing piece"} on the next screen.`}
      </p>

      <div className="mt-6 space-y-2">
        {rows.map((r) => (
          <ChecklistRow
            key={r.key}
            icon={r.icon}
            label={r.label}
            state={r.state}
            detail={r.detail}
          />
        ))}
      </div>

      {envError && (
        <div className="mt-4 rounded-lg bg-hydra-50 px-3 py-2.5 text-[14px] text-hydra">
          {envError}{" "}
          <button
            onClick={() => refreshEnvironment()}
            className="underline underline-offset-2 hover:text-hydra/80"
          >
            Try again
          </button>
        </div>
      )}

      {allChecked && (
        <div className="mt-7 flex justify-end gap-2">
          <Button
            variant="ghost"
            size="md"
            onClick={() => refreshEnvironment()}
          >
            Re-check
          </Button>
          <Button
            variant={fullyReady ? "secondary" : "primary"}
            size="md"
            onClick={goNext}
          >
            {fullyReady ? "Continue" : hasMissing ? "Fix this" : "Continue"}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Row state resolvers ─────────────────────────────────────────────

function resolveClaudeState(env: any, progress: number, loading: boolean): RowState {
  if (progress < 1) return "pending";
  if (loading || !env) return "checking";
  // Only a system install (on the user's PATH) counts as "ready" — the
  // SDK-bundled binary lives inside the app and the user can't invoke it
  // available on PATH as the chat runtime (routed to OpenRouter).
  return env.claude_cli.source === "system" ? "ok" : "missing";
}

function resolveClaudeDetail(env: any, progress: number): string | undefined {
  if (progress < 1) return undefined;
  if (!env) return "Looking for the claude command…";
  if (env.claude_cli.source === "system") {
    return env.claude_cli.version ?? "Installed and ready";
  }
  if (env.claude_cli.source === "sdk_bundled") {
    return "Bundled with OmniWerk — I'll add `claude` to your terminal next.";
  }
  return "Not found on this machine — I'll install it next.";
}

function resolveGitState(env: any, progress: number, loading: boolean): RowState {
  if (progress < 2) return "pending";
  if (loading || !env) return "checking";
  return env.git?.installed ? "ok" : "missing";
}

function resolveGitDetail(env: any, progress: number): string | undefined {
  if (progress < 2) return undefined;
  if (!env?.git) return "Looking for git…";
  if (env.git.installed) return env.git.version ?? "Installed and ready";
  return "Needed on Windows so Claude Code can run its tools.";
}

function resolveNetworkState(env: any, progress: number, loading: boolean): RowState {
  if (progress < 3) return "pending";
  if (loading || !env) return "checking";
  return env.network.reachable ? "ok" : "error";
}

function resolveNetworkDetail(env: any, progress: number): string | undefined {
  if (progress < 3) return undefined;
  if (!env) return "Checking claude.ai reachability…";
  if (env.network.reachable) return "claude.ai is reachable";
  return env.network.message ?? "Couldn't reach claude.ai.";
}

// ─── Icons ───────────────────────────────────────────────────────────

function ClaudeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-4.5 w-4.5">
      <path
        d="M7 6h3l2 4h0l2-4h3v12h-2v-7.5L12 14l-3-3.5V18H7V6Z"
        fill="currentColor"
        opacity="0.9"
      />
    </svg>
  );
}

function GitIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-4.5 w-4.5">
      <circle cx="7" cy="7" r="2.2" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="17" cy="17" r="2.2" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="7" cy="17" r="2.2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M7 9v6M9 17h5.8M15 15.4 9.2 9.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function NetworkIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-4.5 w-4.5">
      <path d="M4.5 9a11 11 0 0 1 15 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M7.5 12a7 7 0 0 1 9 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M10 14.8a3 3 0 0 1 4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="18" r="1" fill="currentColor" />
    </svg>
  );
}
